import { useState } from 'react';
import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { EmptyMessage } from '../../components/EmptyMessage';
import { FilterMenu } from '../../components/FilterMenu';
import { ItemCard } from '../../components/ItemCard';
import { Section, Title } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import { TitleRow } from './AccommodationListTemplate.styles';
import type {
  AccommodationListItem,
  AccommodationListTemplateProps,
} from './AccommodationListTemplate.types';

const META_SEPARATOR = ' · ';

function buildMeta(item: AccommodationListItem): string | undefined {
  const labels = item.occupancyLabels?.filter(Boolean) ?? [];
  return labels.length > 0 ? labels.join(META_SEPARATOR) : undefined;
}

function matchesInclusions(item: AccommodationListItem, selected: string[]): boolean {
  const inclusions = item.inclusions ?? [];
  return selected.every((value) => inclusions.includes(value));
}

export function AccommodationListTemplate({
  items,
  onSelectItem,
  isLoading,
  titleLabel,
  fromPriceLabel,
  soldOutLabel,
  noResultsLabel,
  noImageLabel,
  editSearchLabel,
  onEditSearch,
  columns = 2,
  imageAspectRatio,
  imageMobileAspectRatio,
  inclusionOptions,
  inclusionFilterLabel = 'Filter',
  noFilterResultsLabel,
}: AccommodationListTemplateProps) {
  const [selectedInclusions, setSelectedInclusions] = useState<string[]>([]);

  const optionValues = inclusionOptions?.map((option) => option.value) ?? [];
  const activeInclusions = selectedInclusions.filter((value) => optionValues.includes(value));

  if (activeInclusions.length !== selectedInclusions.length) {
    setSelectedInclusions(activeInclusions);
  }

  const filter = inclusionOptions && inclusionOptions.length > 0 && (
    <FilterMenu
      options={inclusionOptions}
      selectedValues={activeInclusions}
      onChange={setSelectedInclusions}
      label={inclusionFilterLabel}
    />
  );

  const filteredItems =
    activeInclusions.length === 0
      ? items
      : items.filter((item) => matchesInclusions(item, activeInclusions));

  if (isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
          {filter}
        </TitleRow>
        <CardGrid $columns={columns}>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard
              key={index}
              variant="grid"
              aspectRatio={imageAspectRatio}
              mobileAspectRatio={imageMobileAspectRatio}
            />
          ))}
        </CardGrid>
      </Section>
    );
  }

  if (!isLoading && filteredItems.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
          {filter}
        </TitleRow>
        <EmptyMessage>
          {activeInclusions.length > 0 ? noFilterResultsLabel ?? noResultsLabel : noResultsLabel}
        </EmptyMessage>
      </Section>
    );
  }

  return (
    <Section>
      <TitleRow>
        <Title>{titleLabel}</Title>
        {filter}
      </TitleRow>
      <ContentArea>
        <CardGrid $columns={columns}>
          {filteredItems.map((item) => (
            <ItemCard
              key={item.id}
              image={{ src: item.imageUrl, alt: item.name, placeholderLabel: noImageLabel }}
              imageAspectRatio={imageAspectRatio}
              imageMobileAspectRatio={imageMobileAspectRatio}
              title={item.name}
              meta={buildMeta(item)}
              badges={!item.unavailable ? item.badges : undefined}
              imageBadge={item.promotionName || undefined}
              unavailableLabel={
                item.unavailable ? item.unavailableLabel ?? soldOutLabel : undefined
              }
              editSearchLabel={editSearchLabel}
              onEditSearch={onEditSearch}
              price={
                !item.unavailable && item.price
                  ? {
                      amount: item.price.amount,
                      currencyCode: item.price.currencyCode,
                      label: fromPriceLabel,
                      discount: item.discount,
                    }
                  : undefined
              }
              unavailable={item.unavailable}
              ariaLabel={item.name}
              onSelect={() => onSelectItem(item.id)}
            />
          ))}
        </CardGrid>
      </ContentArea>
    </Section>
  );
}
